import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BuildingLibraryIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useTeamId } from '../hooks/useTeamId';
import { PageHeader, StatusBadge, StatCard, LoadingSpinner } from '../components/ui';
import type { BadgeVariant } from '../components/ui';
import { api } from '../lib/api';
import AdvisoryFirmGuide from '../components/help/AdvisoryFirmGuide';

const HEALTH_COLOR = (score: number): string => {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-amber-600';
  return 'text-red-600';
};

const HEALTH_BG = (score: number): string => {
  if (score >= 80) return 'bg-green-50 border-green-200';
  if (score >= 60) return 'bg-amber-50 border-amber-200';
  return 'bg-red-50 border-red-200';
};

const RATING_BADGE: Record<string, BadgeVariant> = {
  Outstanding: 'success',
  Satisfactory: 'info',
  'Needs to Improve': 'warning',
  'Substantial Noncompliance': 'danger',
};

export default function OrganizationDashboardPage() {
  const { teamId } = useTeamId();

  // For now, fetch the current team's org data via a dedicated endpoint
  // This will be expanded when the full org API is built
  const { data: orgData, isLoading } = useQuery({
    queryKey: ['orgDashboard', teamId],
    queryFn: () => api.get(`/teams/${teamId}/organization/dashboard`),
    enabled: !!teamId,
  });
  const org = orgData?.data;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // If no org data, show a placeholder explaining what this page does
  if (!org?.teams?.length) {
    return (
      <div className="space-y-6">
        <div className="flex justify-end">
          <Link to="/teams" className="text-sm font-medium text-primary-600 hover:text-primary-700">
            Manage banks &amp; members →
          </Link>
        </div>
        <PageHeader
          title="Organization Dashboard"
          description="Monitor compliance posture across all client banks from a single view."
        />
        <div className="rounded-lg border bg-white p-8">
          <div className="flex items-start gap-3">
            <BuildingLibraryIcon className="h-8 w-8 flex-shrink-0 text-teal-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Get started as an advisory firm</h3>
              <p className="mt-1 text-sm text-gray-500 max-w-2xl">
                This dashboard monitors compliance health, upcoming exams, and overdue tasks across all your
                client banks from one screen. Here's the engagement lifecycle, start to finish — add your
                first bank and it fills in.
              </p>
            </div>
          </div>
          <AdvisoryFirmGuide className="mt-6" />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/teams"
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              Add your first client bank
            </Link>
            <Link
              to="/how-to"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Open the How-To hub
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const teams = org.teams || [];
  const totalBanks = teams.length;
  const avgHealth = teams.length > 0
    ? Math.round(teams.reduce((sum: number, t: any) => sum + (t.healthScore || 0), 0) / teams.length)
    : 0;
  const overdueCount = teams.reduce((sum: number, t: any) => sum + (t.overdueTasks || 0), 0);
  const upcomingExams = teams.filter((t: any) => t.daysToExam !== null && t.daysToExam <= 90).length;

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Link to="/teams" className="text-sm font-medium text-primary-600 hover:text-primary-700">
          Manage banks &amp; members →
        </Link>
      </div>
      <PageHeader
        title={org.name || 'Organization Dashboard'}
        description={`${totalBanks} client bank${totalBanks !== 1 ? 's' : ''} · ${org.type === 'CONSULTING_FIRM' ? 'Consulting Firm' : 'Bank Holding Company'}`}
      />

      {/* Aggregate Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard icon={BuildingLibraryIcon} label="Client Banks" value={totalBanks} />
        <StatCard icon={ChartBarIcon} label="Avg Health Score" value={avgHealth} />
        <StatCard icon={ExclamationTriangleIcon} label="Overdue Tasks" value={overdueCount} />
        <StatCard icon={ClockIcon} label="Exams Within 90 Days" value={upcomingExams} />
      </div>

      {/* Bank Cards Grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {teams.map((team: any) => (
          <Link
            key={team.id}
            to={`/dashboard?team=${team.id}`}
            className={clsx(
              'rounded-lg border p-5 transition-all hover:shadow-lg cursor-pointer',
              HEALTH_BG(team.healthScore || 0),
            )}
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-semibold text-navy-900">{team.name}</h3>
                <p className="mt-0.5 text-xs text-gray-500">{team.regulator || '—'}</p>
              </div>
              <div className={clsx('text-2xl font-bold tabular-nums', HEALTH_COLOR(team.healthScore || 0))}>
                {team.healthScore ?? '—'}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
              <div>
                <p className="text-gray-500">CRA Rating</p>
                {team.craRating ? (
                  <StatusBadge variant={RATING_BADGE[team.craRating] || 'neutral'}>
                    {team.craRating}
                  </StatusBadge>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </div>
              <div>
                <p className="text-gray-500">Overdue</p>
                <p className={clsx('font-medium', team.overdueTasks > 0 ? 'text-red-600' : 'text-green-600')}>
                  {team.overdueTasks || 0}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Next Exam</p>
                <p className="font-medium text-gray-700">
                  {team.daysToExam !== null ? `${team.daysToExam}d` : '—'}
                </p>
              </div>
            </div>

            {team.alerts && team.alerts.length > 0 && (
              <div className="mt-3 border-t pt-2">
                {team.alerts.slice(0, 2).map((alert: string, i: number) => (
                  <p key={i} className="text-[10px] text-amber-700 flex items-center gap-1">
                    <ExclamationTriangleIcon className="h-3 w-3 flex-shrink-0" />
                    {alert}
                  </p>
                ))}
              </div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
